import React, { forwardRef, useRef, useState } from 'react';
import * as RadixScrollArea from '@radix-ui/react-scroll-area';
import useResizeObserver from '@react-hook/resize-observer';
import classNamesUtil from 'classnames';

import styles from './ScrollArea.module.scss';

export interface IScrollAreaProps extends RadixScrollArea.ScrollAreaProps {
    children: React.ReactNode;
    classNames?: {
        root?: string;
        viewPort?: string;
        content?: string;
    };
}

export const ScrollArea = forwardRef<HTMLDivElement, IScrollAreaProps>(
    ({ className, children, classNames, ...props }, ref) => {
        // Radix does not resize scrollbars when content size changes resulting in ui break
        // Each time content of scrollArea changes, rerender scrollbars to recalculate
        const contentRef = useRef<HTMLDivElement>(null);
        const [scrollbarKey, setScrollbarKey] = useState(true);
        useResizeObserver(contentRef, () => {
            setScrollbarKey((prev) => !prev);
        });

        return (
            <RadixScrollArea.Root
                ref={ref}
                className={classNamesUtil(styles.root, className, classNames?.root)}
                {...props}
            >
                <RadixScrollArea.Viewport asChild className={classNamesUtil(styles.viewPort, classNames?.viewPort)}>
                    <div className={styles.childrenWrapper}>
                        <div ref={contentRef} className={classNamesUtil(styles.content, classNames?.content)}>
                            {children}
                        </div>
                    </div>
                </RadixScrollArea.Viewport>
                <RadixScrollArea.Scrollbar
                    forceMount
                    orientation="horizontal"
                    className={styles.scrollBar}
                    key={`hscrollbar-${scrollbarKey}`}
                >
                    <RadixScrollArea.Thumb className={styles.thumb} />
                </RadixScrollArea.Scrollbar>
                <RadixScrollArea.Scrollbar
                    forceMount
                    orientation="vertical"
                    className={styles.scrollBar}
                    key={`vscrollbar-${scrollbarKey}`}
                >
                    <RadixScrollArea.Thumb className={styles.thumb} />
                </RadixScrollArea.Scrollbar>
                <RadixScrollArea.Corner />
            </RadixScrollArea.Root>
        );
    },
);

ScrollArea.displayName = 'ScrollArea';
